import { Card, Col, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import SubtleBadge from '@/components/common/SubtleBadge';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import { toPersianDigit } from '@/helpers/utils';
import type { AiDetectorAnalysisData } from '@/app/ContentTools/types/aiContentAnalysisApiTypes';
import AiLikenessGauge from './AiLikenessGauge';

type AiDetectorScoreCardProps = {
  data: AiDetectorAnalysisData;
};

const ScoreMetric = ({
  label,
  value,
  icon,
  iconClassName,
  iconSize = '0.85em'
}: {
  label: string;
  value: string;
  icon: IconProp;
  iconClassName?: string;
  iconSize?: string;
}) => (
  <div className="d-flex justify-content-between align-items-center bg-100 border rounded-3 px-3 py-2">
    <span className="d-inline-flex align-items-center gap-1 fs--1 text-600 lh-1">
      <FontAwesomeIcon
        icon={icon}
        className={`d-block flex-shrink-0 ${iconClassName || ''}`}
        style={{ width: iconSize, height: iconSize }}
      />
      <span className="lh-1">{label}</span>
    </span>
    <span className="fw-bold fs-0 lh-1">{value}</span>
  </div>
);

const AiDetectorScoreCard = ({ data }: AiDetectorScoreCardProps) => {
  return (
    <Card>
      <FalconCardHeader
        title={
          <>
            شباهت محتوا
            <HelpTooltip
              placement="top"
              html="این امتیاز نشان‌دهنده میزان شباهت الگوهای نوشتاری به محتوای تولیدشده توسط AI است و به معنی تشخیص قطعی نویسنده متن نیست."
            />
          </>
        }
        titleTag="h6"
        titleClass="d-flex align-items-center"
        className="py-2"
        light
      />
      <Card.Body>
        <AiLikenessGauge
          value={data.human_likeness}
          displayValue={toPersianDigit(`${Math.round(data.human_likeness)}`)}
        />

        <div className="text-center mb-4 ai-likeness-gauge-caption">
          <div className="fw-bold fs-0">{data.verdict}</div>
          <SubtleBadge bg="success" pill className="mt-2">
            اطمینان {data.confidence}
          </SubtleBadge>
        </div>

        <Row className="g-2 mb-3">
          <Col xs={6}>
            <ScoreMetric
              label="انسان"
              icon="user"
              iconClassName="text-success"
              value={toPersianDigit(`${Math.round(data.human_likeness)}%`)}
            />
          </Col>
          <Col xs={6}>
            <ScoreMetric
              label="AI"
              icon="robot"
              iconClassName="text-warning"
              iconSize="1.05em"
              value={toPersianDigit(`${Math.round(data.ai_likeness)}%`)}
            />
          </Col>
        </Row>

        {data.criteria.length > 0 ? (
          <div className="border-top pt-3 mb-3">
            <div className="fs--2 text-500 fw-semi-bold mb-2">معیارها</div>
            {data.criteria.map(item => (
              <div
                key={item.key}
                className="d-flex align-items-center justify-content-between py-1"
              >
                <span className="fs--1 text-700">{item.label}</span>
                <SubtleBadge bg={item.tone} className="fs--2">
                  {item.status}
                </SubtleBadge>
              </div>
            ))}
          </div>
        ) : null}

        {data.human_signals.length > 0 || data.ai_signals.length > 0 ? (
          <div className="border-top pt-3">
            <div className="fs--2 text-500 fw-semi-bold mb-2">نشانه‌ها</div>
            <div className="d-flex flex-column gap-2">
              {data.human_signals.length > 0 ? (
                <div className="bg-100 border rounded-3 p-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="d-inline-flex align-items-center gap-1 fs--1 text-700 fw-semi-bold">
                      <FontAwesomeIcon icon="user" className="text-success" />
                      نشانه‌های انسانی
                    </span>
                    <SubtleBadge bg="success" className="fs--2">
                      {toPersianDigit(`${data.human_signals.length}`)}
                    </SubtleBadge>
                  </div>
                  <ul className="list-unstyled mb-0">
                    {data.human_signals.map(signal => (
                      <li
                        key={signal}
                        className="d-flex align-items-start gap-2 fs--1 text-700 mb-1"
                      >
                        <FontAwesomeIcon
                          icon="circle"
                          className="text-success mt-2 flex-shrink-0"
                          style={{ fontSize: '0.4rem' }}
                        />
                        <span>{signal}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {data.ai_signals.length > 0 ? (
                <div className="bg-100 border rounded-3 p-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="d-inline-flex align-items-center gap-1 fs--1 text-700 fw-semi-bold">
                      <FontAwesomeIcon
                        icon="robot"
                        className="text-warning"
                      />
                      نشانه‌های AI
                    </span>
                    <SubtleBadge bg="warning" className="fs--2">
                      {toPersianDigit(`${data.ai_signals.length}`)}
                    </SubtleBadge>
                  </div>
                  <ul className="list-unstyled mb-0">
                    {data.ai_signals.map(signal => (
                      <li
                        key={signal}
                        className="d-flex align-items-start gap-2 fs--1 text-700 mb-1"
                      >
                        <FontAwesomeIcon
                          icon="circle"
                          className="text-warning mt-2 flex-shrink-0"
                          style={{ fontSize: '0.4rem' }}
                        />
                        <span>{signal}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </Card.Body>
    </Card>
  );
};

export default AiDetectorScoreCard;
